import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * OAuthCallback page — handles the redirect back from GitHub/GitLab OAuth authorization.
 *
 * Route: /oauth/callback/:provider
 *
 * The provider redirects here with query params:
 *   ?code=<authorization_code>&state=<state_json>
 * or on error:
 *   ?error=<error_code>&error_description=<message>&state=<state_json>
 *
 * The state param is a JSON string containing:
 *   { orgId, connectionName, provider }
 *
 * On success: exchanges the code for a token via the backend, then navigates
 * back to the project management page.
 *
 * On error: displays the error and provides a link back.
 */
export default function OAuthCallback() {
  const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [message, setMessage] = useState('');
  const [connectionData, setConnectionData] = useState(null);
  const exchangeAttempted = useRef(false);

  useEffect(() => {
    // Prevent double-execution in React StrictMode
    if (exchangeAttempted.current) return;
    exchangeAttempted.current = true;

    const code = searchParams.get('code');
    const stateParam = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Handle OAuth error (user denied or provider error)
    if (errorParam) {
      setStatus('error');
      setMessage(
        errorDescription ||
        `Authorization was denied or failed: ${errorParam}`
      );
      return;
    }

    // Validate required params
    if (!code) {
      setStatus('error');
      setMessage('No authorization code received from the provider.');
      return;
    }

    // Parse state to recover context
    let stateData = {};
    try {
      stateData = stateParam ? JSON.parse(stateParam) : {};
    } catch (e) {
      setStatus('error');
      setMessage('Invalid state parameter. Please try the authorization again.');
      return;
    }

    const { orgId, connectionName, csrf } = stateData;

    // Validate CSRF token to prevent cross-site request forgery
    const storedCsrf = sessionStorage.getItem('oauth_csrf_token');
    if (!csrf || !storedCsrf || csrf !== storedCsrf) {
      setStatus('error');
      setMessage('Security validation failed (state mismatch). Please try the authorization again from the SCM wizard.');
      return;
    }
    // Clear the CSRF token after validation
    sessionStorage.removeItem('oauth_csrf_token');

    if (!orgId) {
      setStatus('error');
      setMessage('Missing organisation context. Please try the authorization again from the SCM wizard.');
      return;
    }

    // Exchange the code for a token via the backend
    async function exchangeCode() {
      try {
        const redirectUri = `${window.location.origin}/oauth/callback/${provider}`;
        const res = await apiFetch(
          `/api/orgs/${orgId}/scm-connections/oauth/exchange`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              provider: provider,
              code: code,
              connection_name: connectionName || `${provider} OAuth Connection`,
              redirect_uri: redirectUri,
              scm_org_name: stateData.scmOrgName || '',
            }),
          }
        );

        if (res.ok) {
          const data = await res.json();
          setConnectionData(data);
          setStatus('success');
          setMessage('Authorization successful! SCM connection created.');
        } else {
          const errBody = await res.json().catch(() => ({}));
          setStatus('error');
          setMessage(
            errBody.error ||
            `Failed to complete authorization (HTTP ${res.status}). Please try again.`
          );
        }
      } catch (err) {
        setStatus('error');
        setMessage('Network error while completing authorization. Please check your connection and try again.');
      }
    }

    exchangeCode();
  }, [provider, searchParams]);

  const handleGoBack = () => {
    // Navigate back to the main app — the user can re-open the SCM wizard
    navigate('/');
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {status === 'processing' && (
          <>
            <div style={styles.spinner}>⟳</div>
            <h2 style={styles.title}>Completing Authorization</h2>
            <p style={styles.text}>
              Exchanging authorization code with {provider === 'github' ? 'GitHub' : 'GitLab'}...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.title}>Authorization Successful</h2>
            <p style={styles.text}>{message}</p>
            {connectionData && (
              <div style={styles.details}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Connection:</span>
                  <span style={styles.detailValue}>{connectionData.connection_name}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Provider:</span>
                  <span style={styles.detailValue}>{connectionData.provider}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Status:</span>
                  <span style={{ ...styles.detailValue, color: '#10B981' }}>{connectionData.status}</span>
                </div>
              </div>
            )}
            <button onClick={handleGoBack} style={styles.primaryBtn}>
              Return to Dashboard
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={styles.errorIcon}>✕</div>
            <h2 style={styles.title}>Authorization Failed</h2>
            <p style={styles.errorText}>{message}</p>
            <p style={styles.hintText}>
              You can return to the SCM Connection wizard and try again.
            </p>
            <button onClick={handleGoBack} style={styles.primaryBtn}>
              Return to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    padding: 20,
  },
  card: {
    background: '#FFFFFF',
    borderRadius: 16,
    padding: '48px 40px',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    maxWidth: 480,
    width: '100%',
  },
  spinner: {
    fontSize: 48,
    marginBottom: 16,
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
    color: '#3B82F6',
  },
  successIcon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: '#D1FAE5',
    color: '#10B981',
    fontSize: 28,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  errorIcon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: '#FEE2E2',
    color: '#EF4444',
    fontSize: 28,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1E293B',
    margin: '0 0 8px',
  },
  text: {
    fontSize: 14,
    color: '#64748B',
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  errorText: {
    fontSize: 14,
    color: '#EF4444',
    margin: '0 0 12px',
    lineHeight: 1.5,
    background: '#FEE2E2',
    padding: '12px 16px',
    borderRadius: 8,
    border: '1px solid #FECACA',
  },
  hintText: {
    fontSize: 13,
    color: '#64748B',
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  details: {
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    padding: '16px',
    marginBottom: 24,
    textAlign: 'left',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
  },
  detailLabel: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: 500,
  },
  detailValue: {
    fontSize: 13,
    color: '#1E293B',
    fontWeight: 600,
  },
  primaryBtn: {
    padding: '12px 28px',
    borderRadius: 8,
    border: 'none',
    background: '#3B82F6',
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
  },
};
